import React, { useState } from "react";
import {
  doSignInWithEmailAndPassword,
  doSendEmailVerification,
  doPasswordReset,
} from "../firebase/auth";
import { useAuth } from "../contexts/authContext";
import { Navigate, Link } from "react-router-dom";

const Login = () => {
  const { userLoggedIn, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showResendEmail, setShowResendEmail] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setErrorMessage("Please fill in both email and password.");
      return;
    }

    setErrorMessage(""); // Clear previous error messages
    setIsSigningIn(true);
    setShowResendEmail(false);

    try {
      // Call the Firebase sign-in method
      await doSignInWithEmailAndPassword(email, password);
    } catch (error) {
      console.error(error); // Log the error for debugging
      let message = "Failed to log in. Please try again."; // Default message

      // Match specific error codes and set appropriate messages
      switch (error.code) {
        case "auth/user-not-found":
          message = "No user found with this email.";
          break;
        case "auth/wrong-password":
          message = "Incorrect password.";
          break;
        case "auth/invalid-email":
          message = "Invalid email format.";
          break;
        case "auth/invalid-credential":
          message = "Invalid Details.";
          break;
        case "auth/email-not-verified":
          message =
            "Your email is not verified. Please verify your email to continue.";
          setShowResendEmail(true); // Show the resend email button
          break;
        case "auth/network-request-failed":
          message = "Network error. Please check your connection.";
          break;
        case "auth/too-many-requests":
          message = "Too many login attempts. Please try again later.";
          break;
        default:
          message = error.message || message; // Use Firebase's error message if available
      }

      setErrorMessage(message); // Display the resolved error message
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleResendEmailVerification = async () => {
    setErrorMessage(""); // Clear error messages
    try {
      await doSendEmailVerification();
      setErrorMessage(
        "Verification email resent! Please check your email inbox."
      );
      setShowResendEmail(false); // Hide the button after resending
    } catch (error) {
      setErrorMessage("Failed to resend verification email. Please try again.");
    }
  };

 
    const handleForgotPassword = async () => {
      if (!email) {
        setErrorMessage("Please enter your email first.");
        return;
      }
    
      setErrorMessage(""); // Clear previous errors
    
      try {
        await doPasswordReset(email);
        setErrorMessage("Password reset email sent! Check your inbox.");
      } catch (error) {
        console.error(error);
        const errorMessages = {
          "auth/user-not-found": "No account found with this email.",
          "auth/invalid-email": "Invalid email format.",
          "auth/network-request-failed": "Network error. Try again later.",
        };
        setErrorMessage(errorMessages[error.code] || "Failed to send reset email.");
      }
    };
      

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loader">Loading...</div>
      </div>
    );
  }

  if (userLoggedIn) {
    // Use Navigate directly here
    return <Navigate to={"/dashboard"} replace={true} />;
  }

  return (
    <div className="font-lato">
      <div className="hero bg-base-200 min-h-screen backdrop-blur-sm">
        <div className="hero-content flex-col lg:flex-row-reverse">
          <div className="card bg-base-100 w-full max-w-sm shrink-0 shadow-2xl">
            <form className="card-body" onSubmit={handleLogin}>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Email</span>
                </label>
                <input
                  type="email"
                  placeholder="email"
                  className="input input-bordered"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Password</span>
                </label>
                <input
                  type="password"
                  placeholder="password"
                  className="input input-bordered"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <label className="label">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="label-text-alt link link-hover"
                  >
                    Forgot password?
                  </button>
                </label>
              </div>

              {/* Display error message */}
              {errorMessage && (
                <div className="text-red-500 text-sm mt-2">{errorMessage}</div>
              )}

              {/* Resend email verification button */}
              {showResendEmail && (
                <div className="form-control mt-2">
                  <button
                    type="button"
                    onClick={handleResendEmailVerification}
                    className="btn bg-primary-500"
                  >
                    Resend Verification Email
                  </button>
                </div>
              )}

              <div className="form-control mt-2">
                <button
                  type="submit"
                  className={`btn bg-secondary-500 ${
                    isSigningIn ? "loading" : ""
                  }`}
                  disabled={isSigningIn}
                >
                  {isSigningIn ? "Signing in..." : "Login"}
                </button>
              </div>
            </form>

            {/* Register Link */}
            <div className="text-center mb-2">
              <p>
                Don't have an account?{" "}
                <Link to="/register" className="text-secondary-500 underline">
                  Register here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
